import axios from 'axios';
import { BACKEND_URL, HEADERS, STORAGE_KEYS } from 'const';
import AuthService from './AuthService';
import { nanoid } from 'nanoid';

const sessionid = nanoid();
localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionid);

async function getData(path, headers = {}, params = {}, responseType) {
  const resp = await _request(path, {
    method: 'GET',
    ..._genParams(headers, params, responseType),
  });
  return _getResponseData(resp);
}

async function postData(path, data, headers, params, responseType) {
  const resp = await _request(path, {
    method: 'POST',
    data,
    ..._genParams(headers, params, responseType),
  });
  return _getResponseData(resp);
}

async function get(path, headers, params, responseType) {
  const resp = await _request(path, {
    method: 'GET',
    ..._genParams(headers, params, responseType),
  });

  return _getResponse(resp);
}

async function post(path, data, headers, params, responseType) {
  const resp = await _request(path, {
    method: 'POST',
    data,
    ..._genParams(headers, params, responseType),
  });

  return _getResponse(resp);
}

async function destroy(path, data, headers, params, responseType) {
  const resp = await _request(path, {
    method: 'DELETE',
    data,
    ..._genParams(headers, params, responseType),
  });
  return _getResponseData(resp);
}

async function put(path, data) {
  const resp = await _request(path, {
    method: 'PUT',
    data,
    ..._genParams(),
  });
  return _getResponseData(resp);
}

async function patch(path, data) {
  const resp = await _request(path, { method: 'PATCH', data, ..._genParams() });
  return _getResponseData(resp);
}

function extractErrorMessage(data, fallback = 'Request failed') {
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    if (data.message) {
      return typeof data.message === 'object' ? JSON.stringify(data.message) : String(data.message);
    }
    if (data.detail) {
      return typeof data.detail === 'object' ? JSON.stringify(data.detail) : String(data.detail);
    }
    if (data.error) {
      return typeof data.error === 'object' ? JSON.stringify(data.error) : String(data.error);
    }
    if (data.non_field_errors) {
      return Array.isArray(data.non_field_errors) ? data.non_field_errors.join(', ') : String(data.non_field_errors);
    }
    return JSON.stringify(data);
  }
  return String(data);
}

async function _request(path, config) {
  try {
    return await axios({ url: `${BACKEND_URL}${path}`, ...config });
  } catch (error) {
    if (_isInvalidTokenError(error)) {
      await _handleInvalidTokenError();
    } else {
      if (error.response?.data) {
        error.message = extractErrorMessage(error.response.data, error.message);
      }
      throw error;
    }
  }
}

function _genParams(headers = {}, params = {}, responseType = undefined) {
  const authToken = localStorage.getItem(STORAGE_KEYS.TOKEN);

  const localHeaders = {
    ...headers,
    [HEADERS.AUTH]: authToken ? `Token ${authToken}` : headers[HEADERS.AUTH],
    [HEADERS.SESSION_ID]: sessionid,
  };

  if (!headers[HEADERS.SHOP_ID]) {
    const shopId = localStorage.getItem(STORAGE_KEYS.SHOP_ID);
    if (shopId) {
      localHeaders[HEADERS.SHOP_ID] = shopId;
    }
  }

  return {
    headers: localHeaders,
    params,
    responseType,
  };
}

async function _getResponseData(resp) {
  if (resp?.data?.status !== undefined) {
    if (Number(resp.data.status) === 200) {
      return resp.data.data !== undefined ? resp.data.data : resp.data;
    }
    const errorMessage = extractErrorMessage(resp.data, 'Request failed');
    const err = new Error(errorMessage);
    if (resp?.data?.status_code) {
      err.status_code = resp.data.status_code;
    }
    err.response = resp;
    throw err;
  }
  if (resp?.status === 200 || resp?.status === 201) {
    return resp.data;
  }
  const errorMessage = extractErrorMessage(resp?.data, 'Request failed');
  const err = new Error(errorMessage);
  if (resp?.status_code) {
    err.status_code = resp.status_code;
  }
  err.response = resp;
  throw err;
}

async function _getResponse(resp) {
  if (resp?.data?.status === 200) {
    return resp.data;
  }
  if (resp?.status === 200) {
    return { data: resp.data, status: 200 };
  }
  const errorMessage = extractErrorMessage(resp?.data, 'Request failed');
  const err = new Error(errorMessage);
  if (resp?.status_code) {
    err.status_code = resp.status_code;
  }
  err.response = resp;
  throw err;
}

function _isInvalidTokenError(error) {
  return error?.response?.status === 401;
}

async function _handleInvalidTokenError() {
  await AuthService.logout();
  window.location.reload();
}

const BackendService = {
  getData,
  postData,
  get,
  post,
  put,
  patch,
  destroy,
};

export default BackendService;
